import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { requestPatientAccess, getPatientPortal, patientCancelAppointment, patientRescheduleAppointment, getAvailableSlots } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { Calendar, Clock, User, Phone, Stethoscope, LogOut, X, RefreshCw, ChevronLeft } from 'lucide-react';

const statusLabels = {
  PENDIENTE: 'Pendiente',
  CONFIRMADA: 'Confirmada',
  CANCELADA: 'Cancelada',
  NO_SHOW: 'No asistió',
  COMPLETADA: 'Completada',
  REQUIERE_REAGENDAR: 'Por reagendar',
};

const statusColors = {
  PENDIENTE: 'bg-[#E09F3E]',
  CONFIRMADA: 'bg-[#588157]',
  CANCELADA: 'bg-[#9E2A2B]',
  NO_SHOW: 'bg-[#8D99AE]',
  COMPLETADA: 'bg-[#335C67]',
  REQUIERE_REAGENDAR: 'bg-[#9E2A2B]',
};

export default function PatientPortal() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(searchParams.get('token') || localStorage.getItem('patient_token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [whatsapp, setWhatsapp] = useState('');
  
  // Dialogs
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Reschedule
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const fetchPatientData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getPatientPortal(token);
      // Laravel devuelve {data: {...}} para item único
      setPatient(response.data?.data || response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('patient_token');
      setToken(null);
      setIsAuthenticated(false);
      if (error.response?.status === 401) {
        toast.error('Enlace expirado. Solicita uno nuevo.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('patient_token', token);
      fetchPatientData();
    }
  }, [token, fetchPatientData]);

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!whatsapp) {
      toast.error('Ingresa tu número de WhatsApp');
      return;
    }
    setIsLoading(true);
    try {
      const response = await requestPatientAccess(whatsapp);
      // In sandbox mode, we get the token directly
      if (response.data.sandbox_token) {
        setToken(response.data.sandbox_token);
        toast.success('Acceso concedido (modo sandbox)');
      } else {
        toast.success('Se ha enviado un enlace a tu WhatsApp');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al solicitar acceso'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedAppointment) return;
    setIsLoading(true);
    try {
      await patientCancelAppointment(selectedAppointment.id, token);
      toast.success('Cita cancelada');
      setCancelDialogOpen(false);
      fetchPatientData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cancelar'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchSlots = useCallback(async () => {
    if (!rescheduleDate) return;
    try {
      const response = await getAvailableSlots(rescheduleDate);
      // Laravel devuelve {data: [...]}, Axios extrae el data, entonces accedemos a .data.data
      setAvailableSlots(Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar horarios disponibles'));
    }
  }, [rescheduleDate]);

  useEffect(() => {
    if (rescheduleDate) {
      handleFetchSlots();
    }
  }, [rescheduleDate, handleFetchSlots]);

  const handleReschedule = async () => {
    if (!selectedAppointment || !selectedSlot) return;
    setIsLoading(true);
    try {
      await patientRescheduleAppointment(
        selectedAppointment.id,
        token,
        selectedSlot.date,
        selectedSlot.start_time,
        selectedSlot.end_time
      );
      toast.success('Cita reagendada');
      setRescheduleDialogOpen(false);
      setSelectedSlot(null);
      setRescheduleDate('');
      fetchPatientData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al reagendar'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('patient_token');
    setToken(null);
    setIsAuthenticated(false);
    setPatient(null);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-100 shadow-lg">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#335C67] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Stethoscope className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-['Manrope']">Portal del Paciente</CardTitle>
            <CardDescription>
              Ingresa tu WhatsApp para acceder a tus citas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRequestAccess} className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#8D99AE]" />
                  WhatsApp
                </Label>
                <Input
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  placeholder="+528111234567"
                  data-testid="portal-whatsapp"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                disabled={isLoading}
                data-testid="portal-access-btn"
              >
                {isLoading ? 'Solicitando...' : 'Acceder'}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-slate-100 text-center">
              <p className="text-sm text-[#8D99AE] mb-4">¿No tienes cita?</p>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => navigate('/agendar')}
              >
                Agendar nueva cita
              </Button>
            </div>
          </CardContent>
        </Card>
        </div>
        <footer className="py-4 px-6 border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-sm text-[#8D99AE]">
              Desarrollado por{' '}
              <a
                href="https://agsoftweb.com.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#335C67] hover:text-[#244048] font-medium transition-colors"
              >
                Agsoftweb
              </a>
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // Portal dashboard
  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#335C67] rounded-xl flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-[#2B2D42] font-['Manrope']">Consulterest</h1>
              <p className="text-xs text-[#8D99AE]">Portal del Paciente</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="text-[#9E2A2B]"
            onClick={handleLogout}
            data-testid="portal-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Salir
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        {/* Patient Info */}
        <Card className="border-slate-100 mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#335C67] flex items-center justify-center text-white font-bold text-2xl">
                {patient?.first_name?.charAt(0)}{patient?.paternal_surname?.charAt(0)}
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#2B2D42] font-['Manrope']">
                  {patient?.full_name}
                </h2>
                <p className="text-[#5D5C61]">{patient?.whatsapp}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Appointments */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#2B2D42] font-['Manrope']">
              Próximas Citas
            </h3>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => navigate('/agendar')}
              data-testid="portal-new-appointment"
            >
              Nueva cita
            </Button>
          </div>
          
          {patient?.upcoming_appointments?.length > 0 ? (
            <div className="space-y-3">
              {patient.upcoming_appointments.map((apt) => (
                <Card key={apt.id} className="border-slate-100" data-testid={`upcoming-apt-${apt.id}`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#335C67]">
                            {new Date(apt.date + 'T00:00:00').getDate()}
                          </p>
                          <p className="text-xs text-[#8D99AE] uppercase">
                            {new Date(apt.date + 'T00:00:00').toLocaleDateString('es-MX', { month: 'short' })}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium text-[#2B2D42]">{formatDate(apt.date)}</p>
                          <p className="text-sm text-[#5D5C61]">
                            {apt.start_time} - {apt.end_time}
                          </p>
                          {apt.reason && (
                            <p className="text-xs text-[#8D99AE] mt-1">{apt.reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[apt.status]}>
                          {statusLabels[apt.status]}
                        </Badge>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedAppointment(apt);
                              setRescheduleDialogOpen(true);
                            }}
                            data-testid={`reschedule-btn-${apt.id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#9E2A2B]"
                            onClick={() => {
                              setSelectedAppointment(apt);
                              setCancelDialogOpen(true);
                            }}
                            data-testid={`cancel-btn-${apt.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-slate-100">
              <CardContent className="py-8 text-center">
                <Calendar className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
                <p className="text-[#5D5C61]">No tienes citas próximas</p>
                <Button
                  className="mt-4 bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                  onClick={() => navigate('/agendar')}
                >
                  Agendar cita
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Past Appointments */}
        {patient?.past_appointments?.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-[#2B2D42] font-['Manrope'] mb-4">
              Historial de Citas
            </h3>
            <div className="space-y-2">
              {patient.past_appointments.map((apt) => (
                <Card key={apt.id} className="border-slate-100 bg-slate-50/50" data-testid={`past-apt-${apt.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-[#8D99AE]">
                          {new Date(apt.date + 'T00:00:00').toLocaleDateString('es-MX', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </div>
                        <div className="text-sm text-[#5D5C61]">
                          {apt.start_time}
                        </div>
                      </div>
                      <Badge variant="secondary" className={apt.status === 'COMPLETADA' ? 'bg-[#335C67]/10 text-[#335C67]' : ''}>
                        {statusLabels[apt.status]}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Cita</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas cancelar esta cita?
            </DialogDescription>
          </DialogHeader>
          {selectedAppointment && (
            <div className="py-4">
              <div className="bg-[#F4F1EA] rounded-lg p-4">
                <p className="font-medium text-[#2B2D42]">
                  {formatDate(selectedAppointment.date)}
                </p>
                <p className="text-sm text-[#5D5C61]">
                  {selectedAppointment.start_time} - {selectedAppointment.end_time}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              No, mantener
            </Button>
            <Button
              className="bg-[#9E2A2B] hover:bg-[#7F2223]"
              onClick={handleCancel}
              disabled={isLoading}
              data-testid="confirm-cancel-btn"
            >
              {isLoading ? 'Cancelando...' : 'Sí, cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reagendar Cita</DialogTitle>
            <DialogDescription>
              Selecciona una nueva fecha y horario
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Nueva fecha</Label>
              <Input
                type="date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="reschedule-date"
              />
            </div>
            
            {rescheduleDate && (
              <div className="space-y-2">
                <Label>Horario disponible</Label>
                {availableSlots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {availableSlots.map((slot, index) => (
                      <Button
                        key={index}
                        variant={selectedSlot === slot ? 'default' : 'outline'}
                        size="sm"
                        className={selectedSlot === slot ? 'bg-[#335C67]' : ''}
                        onClick={() => setSelectedSlot(slot)}
                        data-testid={`reschedule-slot-${slot.start_time}`}
                      >
                        {slot.start_time}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#8D99AE]">No hay horarios disponibles</p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#335C67] hover:bg-[#244048]"
              onClick={handleReschedule}
              disabled={isLoading || !selectedSlot}
              data-testid="confirm-reschedule-btn"
            >
              {isLoading ? 'Reagendando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <footer className="mt-auto py-4 px-6 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-[#8D99AE]">
            Desarrollado por{' '}
            <a
              href="https://agsoftweb.com.mx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#335C67] hover:text-[#244048] font-medium transition-colors"
            >
              Agsoftweb
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
